import { useMemo } from 'react';

import type { LEProductAccessGroupParams } from './components/InviteTray/LEProductAccessGroup';
import type { ProductDefinition } from './components/InviteTray/productCatalog';
import type { MemberRole, MemberRoleMap } from './utils/constants';
import type { UseMultiLEProductSelectionReturn } from './useMultiLEProductSelection';

const LE_ICON_BACKGROUND = 'rgb(var(--gray5))';

// Stable empty default so callers without held roles (e.g. the invite tray)
// don't invalidate the productRows memo each render.
const EMPTY_UNASSIGNABLE_ROLES: Record<string, Record<string, MemberRoleMap[]>> = {};

type LegalEntity = {
  legalEntityId?: string;
  legalEntityName?: string;
};

type LocalizedProduct = Omit<
  ProductDefinition,
  'titleMessage' | 'descriptionMessage' | 'trayTitleMessage' | 'hideIconInTray'
> & {
  title: string;
  description: string;
  hideIcon: boolean;
};

type UseMultiLEProductAccessEntitiesParams = {
  legalEntities: readonly LegalEntity[];
  localizedProducts: readonly LocalizedProduct[];
  rolesByProductPerEntity: Record<string, Record<string, MemberRoleMap[]>>;
  /**
   * Per-entity, per-product roles the member holds but that are no longer
   * assignable. Folded into each row's roleOptions so they render checked +
   * disabled — the assignable-only rolesByProductPerEntity omits them.
   */
  unassignableRolesByProductPerEntity?: Record<string, Record<string, MemberRoleMap[]>>;
  /**
   * Per-entity editability. Returning false renders that entity's row and its
   * product rows read-only. Omit to leave editability to the group-level
   * `isEditable` prop.
   */
  isEntityEditable?: (legalEntityId: string) => boolean;
  selection: Pick<
    UseMultiLEProductSelectionReturn,
    | 'getProductSelectionForEntity'
    | 'getRolesForEntity'
    | 'handleEntitySelectionToggle'
    | 'handleProductCheckedChange'
    | 'handleProductRoleToggle'
  >;
};

export function useMultiLEProductAccessEntities({
  legalEntities,
  localizedProducts,
  rolesByProductPerEntity,
  unassignableRolesByProductPerEntity = EMPTY_UNASSIGNABLE_ROLES,
  isEntityEditable,
  selection,
}: UseMultiLEProductAccessEntitiesParams): LEProductAccessGroupParams[] {
  const {
    getProductSelectionForEntity,
    getRolesForEntity,
    handleEntitySelectionToggle,
    handleProductCheckedChange,
    handleProductRoleToggle,
  } = selection;

  return useMemo(
    () =>
      legalEntities
        .filter((le): le is LegalEntity & { legalEntityId: string } => Boolean(le.legalEntityId))
        .map((le) => {
          const entityId = le.legalEntityId;
          const selectedRolesByProduct = getRolesForEntity(entityId);

          return {
            isEditable: isEntityEditable?.(entityId),
            entityRow: {
              entityId,
              title: le.legalEntityName || entityId,
              iconName: 'corporation' as const,
              iconBackground: LE_ICON_BACKGROUND,
              selectionState: getProductSelectionForEntity(entityId),
              onSelectionToggle: (isSelected: boolean) =>
                handleEntitySelectionToggle(entityId, isSelected),
            },
            productRows: localizedProducts
              .filter(
                ({ productKey }) =>
                  (rolesByProductPerEntity[entityId]?.[productKey]?.length ?? 0) > 0 ||
                  (unassignableRolesByProductPerEntity[entityId]?.[productKey]?.length ?? 0) > 0,
              )
              .map(({ productKey, ...product }) => ({
                ...product,
                productKey,
                // Assignable options first, then held-but-unassignable roles.
                // (ProductRolePopover re-sorts assignable-first regardless.)
                roleOptions: [
                  ...(rolesByProductPerEntity[entityId]?.[productKey] ?? []),
                  ...(unassignableRolesByProductPerEntity[entityId]?.[productKey] ?? []),
                ],
                selectedRoleValues: selectedRolesByProduct[productKey] ?? [],
                onCheckedChange: (isChecked: boolean) =>
                  handleProductCheckedChange(entityId, productKey, isChecked),
                onRoleToggle: (role: MemberRole, isSelected: boolean) =>
                  handleProductRoleToggle(entityId, productKey, role, isSelected),
              })),
          };
        }),
    [
      getProductSelectionForEntity,
      getRolesForEntity,
      handleEntitySelectionToggle,
      handleProductCheckedChange,
      handleProductRoleToggle,
      isEntityEditable,
      legalEntities,
      localizedProducts,
      rolesByProductPerEntity,
      unassignableRolesByProductPerEntity,
    ],
  );
}
